import { CartItemType, DiscountType, HtmlString, JobClass } from '@breezy/shared'
import { useMemo } from 'react'
import { trpc } from './trpc'

export type MaintenancePlanSavingsBuilderLineItem = {
  cartItemGuid: string
  seq?: number
  name: string
  description?: HtmlString
  itemGuid: string
  quantity: number
  unitPriceUsc: number
  isTaxable: boolean
  isDiscountable: boolean
  itemType: CartItemType
  photoGuid?: string
  photoCdnUrl?: string
  originalPricebookItemGuid?: string
  cartItemCode?: string
  cartItemVariant?: string
  savedToPricebook?: boolean
}

export type MaintenancePlanSavingsBuilderDiscount =
  | {
      type: DiscountType.FLAT
      discountAmountUsc: number
    }
  | {
      type: DiscountType.RATE
      discountRate: number
    }

export type MaintenancePlanSavingsBuilderInput = {
  lineItems?: MaintenancePlanSavingsBuilderLineItem[]
  discounts?: MaintenancePlanSavingsBuilderDiscount[]
  accountGuid?: string
  jobGuid?: string
  locationGuid?: string
  jobClass?: JobClass
  taxRate?: number
}

/**
 * Hook to fetch maintenance plan savings for an invoice or estimate
 * Returns potential savings information if the customer is eligible
 */
export const useMaintenancePlanSavings = (props: {
  invoiceGuid?: string
  estimateGuid?: string
  enabled?: boolean
  builderInput?: MaintenancePlanSavingsBuilderInput
  preferBuilder?: boolean
  unauth?: boolean
}) => {
  const { invoiceGuid, estimateGuid, enabled = true, builderInput, preferBuilder = false, unauth = false } = props

  const builderPayload = useMemo(() => {
    if (!builderInput) {
      return undefined
    }

    const lineItems = builderInput.lineItems?.map(item => ({
      cartItemGuid: item.cartItemGuid,
      seq: item.seq ?? 0,
      name: item.name,
      description: (item.description ?? '') as HtmlString,
      itemGuid: item.itemGuid,
      quantity: item.quantity,
      unitPriceUsc: item.unitPriceUsc,
      isTaxable: item.isTaxable,
      isDiscountable: item.isDiscountable,
      itemType: item.itemType,
      photoGuid: item.photoGuid,
      photoCdnUrl: item.photoCdnUrl,
      originalPricebookItemGuid: item.originalPricebookItemGuid,
      cartItemCode: item.cartItemCode,
      cartItemVariant: item.cartItemVariant,
      savedToPricebook: item.savedToPricebook,
    }))

    const discounts = builderInput.discounts?.map(discount =>
      discount.type === DiscountType.FLAT
        ? ({
            type: DiscountType.FLAT,
            discountAmountUsc: discount.discountAmountUsc ?? 0,
          } as const)
        : ({
            type: DiscountType.RATE,
            discountRate: discount.discountRate ?? 0,
          } as const),
    )

    return {
      lineItems,
      discounts,
      accountGuid: builderInput.accountGuid,
      jobGuid: builderInput.jobGuid,
      locationGuid: builderInput.locationGuid,
      jobClass: builderInput.jobClass,
      taxRate: builderInput.taxRate,
    }
  }, [builderInput])

  const hasBuilderRequirements = useMemo(() => {
    if (!builderPayload) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[useMaintenancePlanSavings] no builder payload', { preferBuilder })
      }
      return false
    }

    return (
      !!builderPayload.accountGuid &&
      !!builderPayload.lineItems?.length &&
      (typeof builderPayload.taxRate === 'number' || builderPayload.taxRate === 0) &&
      (!!builderPayload.jobGuid || !!builderPayload.jobClass)
    )
  }, [preferBuilder, builderPayload])

  const shouldUseBuilder = preferBuilder && hasBuilderRequirements

  // Only enable if we have enough data to request
  const shouldFetch = enabled && (shouldUseBuilder || (!preferBuilder && (!!invoiceGuid || !!estimateGuid)))

  const queryInput = shouldUseBuilder
    ? {
        invoiceGuid: undefined,
        estimateGuid: undefined,
        ...builderPayload,
      }
    : {
        invoiceGuid: preferBuilder ? undefined : invoiceGuid,
        estimateGuid: preferBuilder ? undefined : estimateGuid,
      }

  const standardProcedure = trpc.invoice['invoicing:calculate-maintenance-plan-savings']
  const unauthProcedure = trpc.invoice['unauth:invoicing:calculate-maintenance-plan-savings']

  const queryInputForHook = queryInput as Parameters<typeof standardProcedure.useQuery>[0]
  const commonOptions: Omit<Parameters<typeof standardProcedure.useQuery>[1], 'enabled'> = {
    staleTime: 0,
    cacheTime: 0,
    retry: 0,
    refetchOnMount: 'always',
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
  }

  const officeQuery = standardProcedure.useQuery(queryInputForHook, {
    ...commonOptions,
    enabled: shouldFetch && !unauth,
  })

  const unauthQuery = unauthProcedure.useQuery(queryInputForHook, {
    ...commonOptions,
    enabled: shouldFetch && unauth,
  })

  const query = unauth ? unauthQuery : officeQuery

  const formattedSavings = useMemo(() => {
    if (!query.data || !('potentialSavingsUsc' in query.data)) {
      return null
    }

    const dollars = Math.floor(query.data.potentialSavingsUsc / 100)
    const cents = query.data.potentialSavingsUsc % 100

    if (cents === 0) {
      return `$${dollars}`
    }

    return `$${dollars}.${cents.toString().padStart(2, '0')}`
  }, [query.data])

  return {
    ...query,
    formattedSavings,
    savings: query.data,
  }
}
